import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import bannerService from "../../services/bannerService";

// Async thunks for banner operations
export const fetchBanners = createAsyncThunk(
  "banners/fetchBanners",
  async (params, { rejectWithValue }) => {
    try {
      const response = await bannerService.getAllBanners(params);
      return response.data?.banners || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch banners"
      );
    }
  }
);

export const fetchBannerById = createAsyncThunk(
  "banners/fetchBannerById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await bannerService.getBannerById(id);
      return response.data?.banner || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch banner"
      );
    }
  }
);

export const createBanner = createAsyncThunk(
  "banners/createBanner",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await bannerService.createBanner(formData);
      return response.data?.banner || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create banner"
      );
    }
  }
);

export const updateBanner = createAsyncThunk(
  "banners/updateBanner",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await bannerService.updateBanner(id, formData);
      return response.data?.banner || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update banner"
      );
    }
  }
);

export const deleteBanner = createAsyncThunk(
  "banners/deleteBanner",
  async (id, { rejectWithValue }) => {
    try {
      await bannerService.deleteBanner(id);
      return { id };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete banner"
      );
    }
  }
);

const bannerSlice = createSlice({
  name: "banners",
  initialState: {
    banners: [],
    currentBanner: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setCurrentBanner: (state, action) => {
      state.currentBanner = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch all banners
    builder
      .addCase(fetchBanners.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.banners = action.payload;
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch banner by ID
      .addCase(fetchBannerById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBannerById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBanner = action.payload;
      })
      .addCase(fetchBannerById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create banner
      .addCase(createBanner.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createBanner.fulfilled, (state, action) => {
        state.isCreating = false;
        state.success = "Banner created successfully";
        state.banners.unshift(action.payload);
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // Update banner
      .addCase(updateBanner.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success = "Banner updated successfully";
        const index = state.banners.findIndex(
          (b) => b.id === action.payload.id
        );
        if (index !== -1) {
          state.banners[index] = action.payload;
        }
        if (state.currentBanner?.id === action.payload.id) {
          state.currentBanner = action.payload;
        }
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Delete banner
      .addCase(deleteBanner.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.success = "Banner deleted successfully";
        state.banners = state.banners.filter((b) => b.id !== action.payload.id);
        if (state.currentBanner?.id === action.payload.id) {
          state.currentBanner = null;
        }
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess, setCurrentBanner } =
  bannerSlice.actions;
export default bannerSlice.reducer;
